import React, { useState, useEffect, useCallback, useRef } from 'react';
import SeasonSelect from './SeasonSelect';
import AreaField from './AreaField';
import { CyclingEvent, EventCategory, EventType, CreateEventRequest, DistanceRequest, EventLink, Organizer, CATEGORY_LABELS, EVENT_TYPE_LABELS, EVENT_TYPES_BY_CATEGORY, EVENT_CATEGORY_ORDER, sortEventTypesByLabel, displayBundesland, postalCodeRegionWarning, RegistrationMode, registrationLinkOf, withRegistrationLink , RegistrationState, distanceShortLabel, ownDistanceEventType, eventTypesForCategories, distancesForCategories } from '@radevents/shared';
import BundeslandOptions from './BundeslandOptions';
import EventLinksEditor, { cleanLinks } from './EventLinksEditor';
import RegistrationFields from './RegistrationFields';
import PriceTiersEditor, { PriceTierFormRow, toPriceTiers } from './PriceTiersEditor';
import OrganizerFormModal from './OrganizerFormModal';
import eventService from '../services/eventService';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { analyzeHeroImage, HeroShape } from '../utils/heroImageShape';
import { downloadFromApi, fileSlug } from '../utils/downloadFile';

export interface EventFormValues {
  title: string;
  description: string;
  locationName: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  bundesland: string;
  eventDate: string;
  endDate: string;
  startTime: string;
  startTimeEnd: string;
  /** Teilnehmer der letzten Auflage als Eingabetext; '' = keine Angabe. */
  participants: string;
  organizer: string;
  organizerId?: number | null;
  category: EventCategory | '';
  /** Weitere Kategorien, fuer die die Veranstaltung offen ist — ohne die Leitkategorie. */
  secondaryCategories: EventCategory[];
  eventType: EventType | '';
  organizerUrl: string;
  /** Externe Flyer-URL (nicht die aufgeloeste: ein Upload wird separat verwaltet). */
  flyerUrl: string;
  /** URL des hochgeladenen Flyers, sonst null. Nur fuer Vorschau/Loeschen — wird nicht gespeichert. */
  flyerUploadedUrl: string | null;
  /** Content-Type des hochgeladenen Flyers — entscheidet, ob die Vorschau ein Bild zeigt. */
  flyerContentType: string | null;
  /** Externe Bild-URL (nicht die aufgeloeste: ein Upload wird separat verwaltet). */
  imageUrl: string;
  imageCredit: string;
  /** Bild ist KI-generiert und wird oeffentlich so gekennzeichnet. */
  imageAiGenerated: boolean;
  /** Fokuspunkt in Prozent — was beim Zuschneiden sichtbar bleiben muss. */
  imageFocusX: number;
  imageFocusY: number;
  /** URL des hochgeladenen Bilds, sonst null. Nur fuer Vorschau/Loeschen — wird nicht gespeichert. */
  imageUploadedUrl: string | null;
  fullyBooked: boolean;
  cancelled: boolean;
  tentative: boolean;
  /** Permanentes Event ohne Termin — Datumsfelder entfallen, Ort und PLZ werden optional. */
  permanent: boolean;
  /** Saison als Monate 1–12, beide `null` = ganzjährig (siehe `SeasonSelect`). */
  seasonFrom: number | null;
  seasonUntil: number | null;
  /** Gebiet ohne festen Startort („Erzgebirge"), nur bei `permanent` (siehe `AreaField`). */
  areaName: string;
  /**
   * Anmeldestart als abgeschnittenes ISO-Datum ('2026-12-01', '2026-12', '2027') — leer = keiner.
   * Einreichen darf ihn jeder; zu *sehen* bekommen ihn nur Mods/Admins (die API liefert ihn
   * niemandem sonst), er ist eine interne Angabe.
   */
  registrationStart: string;
  /** Wie man sich anmeldet — steuert Anmelde-Link und Anmeldestart (siehe RegistrationFields). */
  registrationMode: RegistrationMode;
  /** Gesetzter Stand („läuft bereits"/„geschlossen") — Alternative zum Anmeldestart. */
  registrationState: RegistrationState;
  active: boolean;
  distances: DistanceRequest[];
  komootCollectionUrl: string;
  links: EventLink[];
  /** Preisstufen als Eingabetext (siehe PriceTiersEditor). */
  priceTiers: PriceTierFormRow[];
}

export const emptyEventForm: EventFormValues = {
  title: '',
  description: '',
  locationName: '',
  street: '',
  houseNumber: '',
  postalCode: '',
  city: '',
  bundesland: '',
  eventDate: '',
  endDate: '',
  startTime: '',
  startTimeEnd: '',
  participants: '',
  organizer: '',
  organizerId: null,
  category: '' as EventCategory | '',
  secondaryCategories: [] as EventCategory[],
  eventType: '' as EventType | '',
  organizerUrl: '',
  flyerUrl: '',
  flyerUploadedUrl: null,
  flyerContentType: null,
  imageUrl: '',
  imageCredit: '',
  imageAiGenerated: false,
  imageFocusX: 50,
  imageFocusY: 50,
  imageUploadedUrl: null,
  fullyBooked: false,
  cancelled: false,
  tentative: false,
  permanent: false,
  seasonFrom: null,
  seasonUntil: null,
  areaName: '',
  registrationStart: '',
  registrationMode: 'UNKNOWN',
  registrationState: 'UNKNOWN',
  active: true,
  distances: [{ distanceKm: 0, elevationMeters: 0, routeUrl: '', label: '' }],
  komootCollectionUrl: '',
  links: [],
  priceTiers: [],
};

/**
 * Eine vom Einreichenden gewählte GPX-Datei samt der Distanz, zu der sie gehört (nur im
 * Vorschlags-Modus). Die Zuordnung läuft über den Wert und nicht über eine Id, weil die
 * Vorschlags-Distanz erst beim Einreichen entsteht — der Aufrufer sucht sie in der Antwort.
 */
export interface ProposedGpxFile {
  file: File;
  distanceKm: number;
  durationMinutes?: number | null;
}

export type PreparedEventData = CreateEventRequest & {
  active?: boolean;
  /** Beim Vorrücken ins nächste Jahr: den bisherigen Stand als Auflage ablegen (Haken im Formular). */
  newEdition?: boolean;
  /**
   * Vom Einreichenden ausgewähltes Foto (nur im Vorschlags-Modus). Der Aufrufer lädt es hoch,
   * nachdem der Vorschlag angelegt ist — vorher gibt es keine ID, an der das Bild hängen kann.
   */
  imageFile?: File | null;
  /**
   * Vom Einreichenden gewählter Flyer (nur im Vorschlags-Modus) — wie das Foto erst nach dem
   * Einreichen hochladbar, und wie es an Premium des Veranstalters gebunden.
   */
  flyerFile?: File | null;
  /**
   * Vom Einreichenden gewählte Strecken (nur im Vorschlags-Modus) — genau wie das Foto erst nach
   * dem Einreichen hochladbar.
   */
  gpxFiles?: ProposedGpxFile[];
};

/**
 * Das Event-Formular selbst — ohne Overlay, damit es auch **in der Seite** stehen kann.
 *
 * Auf der Event-Seite wird seit dem 07.09.2026 inline bearbeitet: das Formular ersetzt dort den
 * Inhalt, statt als Fenster darueber zu liegen. Die Admin-Listen behalten das Fenster
 * (`EventFormModal`), weil man dort in der Liste bleiben will. Ein Formular, zwei Huellen — ein
 * zweiter Nachbau der Felder waere beim naechsten Feld auseinandergelaufen.
 */
export interface EventFormProps {
  modalTitle: string;
  saveLabel?: string;
  initialValues: EventFormValues;
  editingId?: number | null;
  organizers: Organizer[];
  onOrganizersRefresh: () => void;
  saving: boolean;
  onSave: (data: PreparedEventData) => Promise<void>;
  onClose: () => void;
  extraFooter?: React.ReactNode;
  /** „↗ Öffnen" neben dem Titel — nur im Fenster sinnvoll; inline steht man schon auf der Seite. */
  showOpenLink?: boolean;
  /** Im Fenster: auf dem Handy bleibt die Knopfleiste unten stehen (`admin-dialog__aktionen`). */
  imDialog?: boolean;
  /**
   * „Termin dort eintragen": aus einem neuen Event in die Bearbeitung des gefundenen wechseln.
   *
   * Nur der Veranstalter-Bereich setzt das, weil nur dort feststeht, dass der Einreichende dieses
   * Event auch bearbeiten darf. Ohne die Funktion nennt der Kasten das vorhandene Event trotzdem.
   */
  onUseAsEdition?: (event: CyclingEvent) => void;
  /**
   * Das Feld, wegen dem das Formular geöffnet wurde — es bekommt den Fokus und kurz einen Ring.
   *
   * Gesetzt vom Weg „Neuen Termin eintragen“: dort geht es um **ein** Datum, und ohne Markierung
   * sucht der Veranstalter es zwischen zwanzig Feldern.
   */
  focusField?: 'eventDate';
}

interface EventFormModalProps extends EventFormProps {
  show: boolean;
}

export const EventForm: React.FC<EventFormProps> = ({
  modalTitle,
  saveLabel = 'Speichern',
  initialValues,
  editingId,
  organizers,
  onOrganizersRefresh,
  saving,
  onSave,
  onClose,
  extraFooter,
  showOpenLink = false,
  imDialog = false,
  onUseAsEdition,
  focusField,
}) => {
  const { user, isAdminOrModerator } = useAuth();
  const [form, setForm] = useState<EventFormValues>(initialValues);
  const [organizerMode, setOrganizerMode] = useState<'existing' | 'freetext'>('freetext');
  const [selectedOrganizerId, setSelectedOrganizerId] = useState<number | null>(null);
  const [organizerSearch, setOrganizerSearch] = useState('');
  // "Neu anlegen" öffnet das volle Veranstalter-Formular (gleiche Felder wie beim Bearbeiten).
  const [showNewOrganizerModal, setShowNewOrganizerModal] = useState(false);
  const [similarEvents, setSimilarEvents] = useState<CyclingEvent[]>([]);
  /**
   * Dasselbe Event in einem anderen Jahr (21.09.2026 → 23.09.2026).
   *
   * Der Duplikat-Check oben vergleicht den Termin auf den Tag genau und ist über Jahresgrenzen
   * deshalb blind. Genau dort entsteht der Fehler: ein Verein trägt seinen Termin fürs nächste
   * Jahr als neues Event ein, und die Auflage verliert Vormerkungen, Zahlen und ihre Adresse bei
   * Google. Nur beim Anlegen — beim Bearbeiten hängt der Termin schon am richtigen Event.
   */
  const [editionCandidates, setEditionCandidates] = useState<CyclingEvent[]>([]);
  const [komootImporting, setKomootImporting] = useState(false);
  const [komootError, setKomootError] = useState<string | null>(null);
  // Per-distance GPX state: key = distance.id (only set for already-persisted rows)
  const [gpxUploadingId, setGpxUploadingId] = useState<number | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [flyerUploading, setFlyerUploading] = useState(false);
  /** Was der Fokus-Picker zeigt: das hochgeladene Bild, sonst die externe URL. */
  const heroPreviewSrc = form.imageUploadedUrl || form.imageUrl || null;
  // Vorschau des Flyers in derselben Rangfolge wie auf der Event-Seite: Upload vor Link. Ob sich
  // die Datei einbetten laesst, steht beim Upload fest (Content-Type) und laesst sich beim Link
  // nur an der Endung ahnen — deshalb nicht ueber isFlyerImage(), das ein Event erwartet.
  const flyerPreviewSrc = form.flyerUploadedUrl || form.flyerUrl || null;
  const flyerPreviewIsImage = form.flyerUploadedUrl
    ? (form.flyerContentType ?? '').indexOf('image/') === 0
    : !/\.pdf($|[?#])/i.test(form.flyerUrl);
  // Wie die Event-Seite das Bild einsetzen wird — dieselbe Erkennung wie dort, damit die
  // Vorschau nicht etwas anderes zeigt als die Seite (Foto zugeschnitten, Logo als Kachel).
  const [previewShape, setPreviewShape] = useState<HeroShape | null>(null);
  useEffect(() => {
    setPreviewShape(null);
    if (!heroPreviewSrc) return;
    let cancelled = false;
    analyzeHeroImage(heroPreviewSrc)
      .then(shape => { if (!cancelled) setPreviewShape(shape); })
      .catch(() => { if (!cancelled) setPreviewShape('photo'); });
    return () => { cancelled = true; };
  }, [heroPreviewSrc]);
  // Vorschlags-Modus: Bild und Nachweis gehören zum eingereichten Foto, nicht zum Event —
  // deshalb eigener State und nicht form.imageUrl/imageCredit (die tragen den Bestand).
  const canManageImages = isAdminOrModerator();
  const [proposedImage, setProposedImage] = useState<File | null>(null);
  const [proposedImagePreview, setProposedImagePreview] = useState<string | null>(null);
  const [proposedImageCredit, setProposedImageCredit] = useState('');
  const [proposedImageError, setProposedImageError] = useState<string | null>(null);
  const [proposedFlyer, setProposedFlyer] = useState<File | null>(null);
  const [proposedFlyerError, setProposedFlyerError] = useState<string | null>(null);
  const [gpxHasFileById, setGpxHasFileById] = useState<Record<number, boolean>>({});
  /**
   * Vorschlags-Modus: gewählte GPX-Dateien je Distanz-Zeile (Schlüssel = Index in form.distances).
   * Anders als beim Admin-Upload wird hier nichts sofort hochgeladen — ohne Freigabe darf keine
   * fremde Strecke am Event hängen. Sie gehen erst mit dem Vorschlag raus (siehe onSave).
   */
  const [proposedGpxByRow, setProposedGpxByRow] = useState<Record<number, File>>({});
  /**
   * Vorrücken ins nächste Jahr (21.09.2026): lag der bisherige Termin zurück und liegt der neue
   * dahinter, legt das Speichern den alten Stand als Auflage ab — Zahlen beginnen neu, der
   * Flyer wandert mit. Der Haken ist von selbst gesetzt und lässt sich für den Ausnahmefall
   * abwählen (ein nachgetragener, nur verschobener Termin). Dieselbe Regel wie im Backend.
   */
  const [newEdition, setNewEdition] = useState(true);
  const todayIso = new Date().toISOString().slice(0, 10);
  // Ein permanentes Event hat keine Auflagen (siehe EventEditionService.wouldRollOver).
  // Auch nicht beim Wechsel permanent -> Termin: der „alte Termin" wäre der Platzhalter 1970.
  const rollsOver = !form.permanent && !initialValues.permanent && !!editingId && !!initialValues.eventDate && !!form.eventDate &&
    initialValues.eventDate < todayIso && form.eventDate > initialValues.eventDate;

  /**
   * Das Feld, wegen dem das Formular offen ist: Fokus hin, Ring an, nach ein paar Sekunden aus.
   *
   * Der Ring endet von selbst, weil er nur die Frage „wo soll ich hier hin?“ beantwortet — danach
   * wäre er eine Auszeichnung ohne Aussage (dieselbe Überlegung wie beim Rahmen um ein
   * angesprungenes Event in EventsTab). `preventScroll` nicht: hineinzuspringen ist hier richtig.
   */
  const eventDateRef = useRef<HTMLInputElement>(null);
  const [calledField, setCalledField] = useState<'eventDate' | null>(null);
  useEffect(() => {
    if (focusField !== 'eventDate') { setCalledField(null); return; }
    setCalledField('eventDate');
    eventDateRef.current?.focus();
    const timer = setTimeout(() => setCalledField(null), 2600);
    return () => clearTimeout(timer);
    // initialValues wechselt bei jedem Oeffnen — genau dann soll der Ring wieder angehen.
  }, [focusField, initialValues]);

  // Re-initialize form when initialValues change (new open). Das Fenster haengt das Formular
  // erst beim Oeffnen ein, deshalb genuegt initialValues als Ausloeser.
  useEffect(() => {
    setForm(initialValues);
    setNewEdition(true);
    if (initialValues.organizerId) {
      setOrganizerMode('existing');
      setSelectedOrganizerId(initialValues.organizerId);
    } else {
      setOrganizerMode('freetext');
      setSelectedOrganizerId(null);
    }
    setOrganizerSearch('');
    setShowNewOrganizerModal(false);
    setSimilarEvents([]);
    setProposedImage(null);
    setProposedImagePreview(null);
    setProposedImageCredit('');
    setProposedImageError(null);
    setProposedFlyer(null);
    setProposedFlyerError(null);
    setProposedGpxByRow({});
  }, [initialValues]);

  // Object-URL der Vorschau wieder freigeben, sobald ein anderes Bild gewählt wird.
  useEffect(() => () => { if (proposedImagePreview) URL.revokeObjectURL(proposedImagePreview); }, [proposedImagePreview]);

  const pickProposedImage = (file: File | null) => {
    setProposedImageError(null);
    if (!file) { setProposedImage(null); setProposedImagePreview(null); return; }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setProposedImageError('Nur JPEG, PNG oder WebP.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setProposedImageError('Das Bild ist zu groß (max. 2 MB). Bitte vorher verkleinern.');
      return;
    }
    setProposedImage(file);
    setProposedImagePreview(URL.createObjectURL(file));
  };

  const pickProposedFlyer = (file: File | null) => {
    setProposedFlyerError(null);
    if (!file) { setProposedFlyer(null); return; }
    if (!['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setProposedFlyerError('Nur PDF, JPEG, PNG oder WebP.');
      return;
    }
    // Dieselbe Grenze wie der Server (FlyerBytes.MAX_MB), nur früher gesagt.
    if (file.size > 4 * 1024 * 1024) {
      setProposedFlyerError('Der Flyer ist zu groß (max. 4 MB).');
      return;
    }
    setProposedFlyer(file);
  };

  const checkDuplicates = useCallback(async (title: string, eventDate: string, bundesland: string, category?: EventCategory) => {
    if (!title.trim() || !eventDate || !bundesland) { setSimilarEvents([]); setEditionCandidates([]); return; }
    try {
      const results = await eventService.findSimilar(title, eventDate, bundesland, editingId ?? undefined, category);
      setSimilarEvents(results);
    } catch { setSimilarEvents([]); }
    if (editingId) { setEditionCandidates([]); return; }
    try {
      setEditionCandidates(await eventService.findEditions(title, eventDate, bundesland, undefined, category));
    } catch { setEditionCandidates([]); }
  }, [editingId]);

  useEffect(() => {
    const timer = setTimeout(() => checkDuplicates(form.title, form.eventDate, form.bundesland, form.category || undefined), 500);
    return () => clearTimeout(timer);
  }, [form.title, form.eventDate, form.bundesland, form.category, checkDuplicates]);

  const handleSave = async () => {
    if (!form.category) {
      alert('Bitte zuerst eine Kategorie wählen');
      return;
    }
    let organizerName: string | undefined = form.organizer || undefined;
    let organizerId: number | undefined = undefined;

    if (organizerMode === 'existing') {
      organizerId = selectedOrganizerId ?? undefined;
      organizerName = undefined;
    } else {
      organizerName = form.organizer || undefined;
    }

    // Eine Variante zählt, wenn sie eine Distanz ODER ein Zeitlimit trägt (24h-Rennen: km leer).
    const keptRows = form.distances
      .map((d, idx) => ({ d, idx }))
      .filter(({ d }) => d.distanceKm > 0 || (d.durationMinutes ?? 0) > 0);
    const distances = keptRows.map(({ d }) => d);
    // Nur Dateien zu Zeilen, die auch eingereicht werden — eine leer geräumte Zeile nimmt ihre
    // Strecke nicht mit.
    const gpxFiles: ProposedGpxFile[] = keptRows
      .filter(({ idx }) => proposedGpxByRow[idx])
      .map(({ d, idx }) => ({ file: proposedGpxByRow[idx], distanceKm: d.distanceKm, durationMinutes: d.durationMinutes }));

    await onSave({
      title: form.title,
      description: form.description || undefined,
      locationName: form.locationName || undefined,
      street: form.street || undefined,
      houseNumber: form.houseNumber || undefined,
      postalCode: form.postalCode,
      city: form.city,
      bundesland: form.bundesland,
      eventDate: form.eventDate,
      endDate: form.endDate || undefined,
      startTime: form.startTime || undefined,
      startTimeEnd: form.startTimeEnd || undefined,
      // Leer = 0: beim Speichern loescht das die Angabe, beim Vorschlag heisst es "keine Angabe".
      participants: form.participants ? parseInt(form.participants, 10) || 0 : 0,
      organizer: organizerName,
      organizerId,
      category: form.category as EventCategory,
      secondaryCategories: form.secondaryCategories,
      eventType: form.eventType ? form.eventType as EventType : undefined,
      organizerUrl: form.organizerUrl || undefined,
      flyerUrl: form.flyerUrl || undefined,
      // Leerstring statt undefined: null bedeutet im Backend "nicht mitgeschickt"
      // (Bestand behalten) — Leeren des Feldes muss aber loeschen.
      imageUrl: form.imageUrl,
      // Im Vorschlags-Modus gehört der Nachweis zum eingereichten Foto — sonst der des Events.
      imageCredit: proposedImage ? proposedImageCredit : form.imageCredit,
      imageFile: proposedImage,
      flyerFile: proposedFlyer,
      imageAiGenerated: form.imageAiGenerated,
      imageFocusX: form.imageFocusX,
      imageFocusY: form.imageFocusY,
      fullyBooked: form.fullyBooked,
      cancelled: form.cancelled,
      tentative: form.tentative,
      permanent: form.permanent,
      // Die Saison gehört zum permanenten Event; ein Termin schickt ausdrücklich „keine".
      seasonFrom: form.permanent ? form.seasonFrom : null,
      seasonUntil: form.permanent ? form.seasonUntil : null,
      // Wie die Saison: nur am permanenten Event; leer heißt „kein Gebiet".
      areaName: form.permanent ? (form.areaName.trim() || null) : null,
      // Leerstring statt undefined: sonst hiesse "geleert" im Backend "nicht mitgeschickt" und der
      // alte Wert bliebe stehen. Im Vorschlags-Weg ist das egal (dort loescht nichts), beim
      // direkten Speichern ist es der Unterschied zwischen Loeschen und Behalten.
      registrationStart: form.registrationStart,
      registrationMode: form.registrationMode,
      registrationState: form.registrationState,
      distances: distances.map(d => ({ ...d, routeUrl: d.routeUrl || undefined, label: d.label || undefined, durationMinutes: d.durationMinutes && d.durationMinutes > 0 ? d.durationMinutes : undefined, startTime: d.startTime || null, eventType: ownDistanceEventType(form.eventType, d.eventType) as EventType | null })),
      komootCollectionUrl: form.komootCollectionUrl || undefined,
      links: cleanLinks(form.links),
      // Immer mitgeschickt: das Formular zeigt alle Stufen des Events, eine leere Liste heißt
      // hier wirklich „gelöscht".
      priceTiers: toPriceTiers(form.priceTiers),
      active: form.active,
      gpxFiles,
      newEdition: rollsOver ? newEdition : undefined,
    });
  };

  const addDistance = () =>
    setForm(f => ({ ...f, distances: [...f.distances, { distanceKm: 0, elevationMeters: 0, routeUrl: '', label: '' }] }));

  const removeDistance = (idx: number) => {
    setForm(f => ({ ...f, distances: f.distances.filter((_, i) => i !== idx) }));
    // Die gewählten Strecken hängen am Zeilen-Index — nach dem Entfernen nachrücken, sonst
    // gehörte die Datei plötzlich zur falschen Distanz.
    setProposedGpxByRow(s => {
      const next: Record<number, File> = {};
      Object.keys(s).forEach(key => {
        const i = Number(key);
        if (i < idx) next[i] = s[i];
        else if (i > idx) next[i - 1] = s[i];
      });
      return next;
    });
  };

  const updateDistance = (idx: number, field: keyof DistanceRequest, value: number | string | null) =>
    setForm(f => ({ ...f, distances: f.distances.map((d, i) => i === idx ? { ...d, [field]: value } : d) }));

  /** Vorschlags-Modus: Datei nur merken. Hochgeladen wird sie erst mit dem Vorschlag. */
  const pickProposedGpx = (idx: number, file: File | null) => {
    if (file && file.size > 5 * 1024 * 1024) {
      alert('GPX-Datei zu groß (max 5 MB).');
      return;
    }
    setProposedGpxByRow(s => {
      const next = { ...s };
      if (file) next[idx] = file; else delete next[idx];
      return next;
    });
  };

  const uploadGpx = async (distanceId: number, file: File) => {
    if (!editingId) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('GPX-Datei zu groß (max 5 MB).');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setGpxUploadingId(distanceId);
    try {
      await api.post(`/events/${editingId}/distances/${distanceId}/gpx`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setGpxHasFileById(s => ({ ...s, [distanceId]: true }));
      setForm(f => ({
        ...f,
        distances: f.distances.map(d => d.id === distanceId ? { ...d, hasGpx: true } : d),
      }));
    } catch (e: any) {
      alert(`GPX-Upload fehlgeschlagen: ${e?.response?.data?.error || e?.message || 'unbekannter Fehler'}`);
    } finally {
      setGpxUploadingId(null);
    }
  };

  const uploadImage = async (file: File) => {
    if (!editingId) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Bild zu groß (max. 2 MB). Bitte vorher verkleinern.');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setImageUploading(true);
    try {
      const res = await api.post(`/events/${editingId}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, imageUploadedUrl: res.data?.imageUrl ?? null }));
    } catch (e: any) {
      alert(`Bild-Upload fehlgeschlagen: ${e?.response?.data?.error || e?.message || 'unbekannter Fehler'}`);
    } finally {
      setImageUploading(false);
    }
  };

  const deleteImage = async () => {
    if (!editingId) return;
    if (!window.confirm('Hochgeladenes Bild wirklich löschen?')) return;
    try {
      await api.delete(`/events/${editingId}/image`);
      setForm(f => ({ ...f, imageUploadedUrl: null }));
    } catch (e: any) {
      alert(`Löschen fehlgeschlagen: ${e?.response?.data?.error || e?.message || 'unbekannter Fehler'}`);
    }
  };

  const uploadFlyer = async (file: File) => {
    if (!editingId) return;
    if (file.size > 4 * 1024 * 1024) {
      alert('Flyer zu groß (max. 4 MB). Bitte vorher verkleinern.');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setFlyerUploading(true);
    try {
      const res = await api.post(`/events/${editingId}/flyer`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({
        ...f,
        flyerUploadedUrl: res.data?.flyerUrl ?? null,
        flyerContentType: res.data?.flyerContentType ?? null,
      }));
    } catch (e: any) {
      alert(`Flyer-Upload fehlgeschlagen: ${e?.response?.data?.error || e?.message || 'unbekannter Fehler'}`);
    } finally {
      setFlyerUploading(false);
    }
  };

  const deleteFlyer = async () => {
    if (!editingId) return;
    if (!window.confirm('Hochgeladenen Flyer wirklich löschen?')) return;
    try {
      await api.delete(`/events/${editingId}/flyer`);
      setForm(f => ({ ...f, flyerUploadedUrl: null, flyerContentType: null }));
    } catch (e: any) {
      alert(`Löschen fehlgeschlagen: ${e?.response?.data?.error || e?.message || 'unbekannter Fehler'}`);
    }
  };

  const deleteGpx = async (distanceId: number) => {
    if (!editingId) return;
    if (!window.confirm('GPX-Datei wirklich löschen?')) return;
    try {
      await api.delete(`/events/${editingId}/distances/${distanceId}/gpx`);
      setGpxHasFileById(s => ({ ...s, [distanceId]: false }));
      setForm(f => ({
        ...f,
        distances: f.distances.map(d => d.id === distanceId ? { ...d, hasGpx: false } : d),
      }));
    } catch (e: any) {
      alert(`Löschen fehlgeschlagen: ${e?.response?.data?.error || e?.message || 'unbekannter Fehler'}`);
    }
  };

  const distanceHasGpx = (d: DistanceRequest): boolean => {
    if (d.id != null && gpxHasFileById[d.id] != null) return gpxHasFileById[d.id];
    return !!d.hasGpx;
  };

  /**
   * Basis des Dateinamens für alles, was an diesem Event hängt: Titel plus Id.
   *
   * Die Id steht mit drin, weil zwei Ausgaben derselben Veranstaltung denselben Titel tragen —
   * im Download-Ordner wären sie sonst nicht auseinanderzuhalten.
   */
  const downloadBaseName = () => `${fileSlug(form.title, 'event')}-${editingId}`;

  /**
   * Bild, Flyer und GPX gehen über Axios und nicht über einen `<a href download>`: Bild und Flyer
   * eines Entwurfs liefert die öffentliche Route nur an Redaktion und Admins aus
   * (`EventController.getImage`), und ein Link könnte den Token nicht mitschicken — der Download
   * wäre je nach Status still leer. Die GPX-Route ist offen, geht aber denselben Weg, damit die
   * Dateinamen einer Regel folgen.
   */
  const downloadEventFile = async (path: string, name: string, what: string) => {
    try {
      await downloadFromApi(path, name);
    } catch {
      alert(`${what} konnte nicht geladen werden.`);
    }
  };

  const downloadGpx = (d: DistanceRequest) =>
    downloadEventFile(
      `/events/${editingId}/distances/${d.id}/gpx`,
      `${downloadBaseName()}-${fileSlug(distanceShortLabel(d), 'strecke')}`,
      'Die GPX-Datei',
    );

  const importKomootCollection = async () => {
    if (!form.komootCollectionUrl) return;
    if (!user) {
      setKomootError('Bitte melde dich an, um Komoot Collections zu importieren.');
      return;
    }
    setKomootImporting(true);
    setKomootError(null);
    try {
      const res = await api.post('/events/komoot-preview', { url: form.komootCollectionUrl });
      const tours: Array<{ name: string; distanceKm: number; elevationMeters: number; komootUrl: string }> = res.data;
      if (tours.length === 0) {
        setKomootError('Keine Touren in dieser Collection gefunden.');
        return;
      }
      setForm(f => ({
        ...f,
        distances: tours.map(t => ({
          distanceKm: t.distanceKm,
          elevationMeters: t.elevationMeters,
          routeUrl: t.komootUrl,
          label: t.name,
        })),
      }));
    } catch (e: any) {
      setKomootError(e.response?.data?.error || 'Fehler beim Laden der Komoot Collection');
    } finally {
      setKomootImporting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    background: 'var(--input-bg)',
    border: '1px solid var(--input-border)',
    color: 'var(--text-primary)',
  };

  return (
    <>
      <div>
        <div className="flex items-center justify-between gap-3 mb-6">
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {modalTitle}
          </h2>
          {showOpenLink && editingId && (
            <a
              href={`/events/${editingId}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Event in neuem Tab öffnen"
              className="inline-flex items-center gap-1 shrink-0 rounded-lg px-3 py-1.5 text-sm font-semibold no-underline"
              style={{
                background: 'var(--surface-bg)', border: '1px solid var(--surface-border)',
                color: 'var(--text-secondary)',
              }}
            >
              ↗ Öffnen
            </a>
          )}
        </div>

        {editionCandidates.length > 0 && (
          <div className="mb-4 p-3 rounded-lg" data-testid="edition-candidates"
            style={{ background: 'var(--page-bg)', border: '1px solid var(--surface-border)' }}>
            <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
              Dieses Event gibt es schon
            </p>
            <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
              Trag den neuen Termin am vorhandenen Event ein, statt ein zweites anzulegen. Dann
              bleiben die Vormerkungen, die Aufrufe und die Adresse bei Google erhalten, und der
              alte Termin wird als frühere Auflage abgelegt.
            </p>
            {editionCandidates.map(ev => (
              <div key={ev.id} className="flex flex-wrap items-center gap-2 mt-1.5">
                <span className="text-xs" style={{ color: 'var(--text-primary)' }}>
                  {ev.title}, {new Date(ev.eventDate + 'T00:00:00').toLocaleDateString('de-DE')} in {displayBundesland(ev.bundesland)}
                </span>
                {onUseAsEdition ? (
                  <button type="button" onClick={() => onUseAsEdition(ev)}
                    className="px-2.5 py-1 rounded-md text-xs font-semibold"
                    style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)', color: 'var(--text-primary)' }}>
                    Termin dort eintragen
                  </button>
                ) : (
                  <a href={`/events/${ev.id}`} target="_blank" rel="noreferrer"
                    className="px-2.5 py-1 rounded-md text-xs font-semibold"
                    style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)', color: 'var(--text-secondary)' }}>
                    Event ansehen
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {similarEvents.length > 0 && (
          <div className="mb-4 p-3 rounded-lg" style={{ background: '#fef3c7', border: '1px solid #fde68a' }}>
            <p className="text-sm font-semibold mb-1" style={{ color: '#92400e' }}>
              Mögliches Duplikat gefunden
            </p>
            {similarEvents.map(ev => (
              <p key={ev.id} className="text-xs" style={{ color: '#78350f' }}>
                &quot;{ev.title}&quot; — {new Date(ev.eventDate + 'T00:00:00').toLocaleDateString('de-DE')} in {displayBundesland(ev.bundesland)}
              </p>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Titel</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div className="sm:col-span-2 flex items-center gap-2">
            <input type="checkbox" id="permanentModal" checked={form.permanent}
              onChange={e => setForm(f => ({ ...f, permanent: e.target.checked, eventDate: '', endDate: '', seasonFrom: null, seasonUntil: null, areaName: '', ...(e.target.checked ? { tentative: false } : {}) }))}
              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
            <label htmlFor="permanentModal" className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              Permanentes Event (ohne festen Termin)
            </label>
          </div>
          {form.permanent && (<>
            <SeasonSelect idPrefix="eventForm" from={form.seasonFrom} until={form.seasonUntil}
              onChange={(seasonFrom, seasonUntil) => setForm(f => ({ ...f, seasonFrom, seasonUntil }))}
              labelClassName="block text-xs font-semibold mb-1" labelStyle={{ color: 'var(--form-label)' }}
              selectClassName="w-full px-3 py-2 rounded-lg text-sm" selectStyle={inputStyle} />
            <AreaField idPrefix="eventForm" value={form.areaName} onChange={areaName => setForm(f => ({ ...f, areaName }))}
              labelClassName="block text-xs font-semibold mb-1" labelStyle={{ color: 'var(--form-label)' }}
              inputClassName="w-full px-3 py-2 rounded-lg text-sm" inputStyle={inputStyle} />
          </>)}
          {!form.permanent && (<>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Startdatum</label>
            <input type="date" ref={eventDateRef} value={form.eventDate}
              onChange={e => setForm(f => ({ ...f, eventDate: e.target.value }))}
              className={`w-full px-3 py-2 rounded-lg text-sm${calledField === 'eventDate' ? ' field-called' : ''}`}
              style={calledField === 'eventDate'
                ? { ...inputStyle, borderColor: 'var(--focus-color)' }
                : inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Enddatum (optional, mehrtägig)</label>
            <input type="date" value={form.endDate} min={form.eventDate || undefined} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          </>)}
          {rollsOver && isAdminOrModerator() && (
            <div className="sm:col-span-2 rounded-lg px-3 py-2.5 text-sm"
              style={{ background: 'var(--page-bg)', border: '1px solid var(--surface-border)' }}
              data-testid="new-edition">
              <label className="flex items-start gap-2 cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                <input type="checkbox" checked={newEdition} onChange={e => setNewEdition(e.target.checked)} className="mt-0.5" />
                <span>
                  <span className="font-semibold">Neue Auflage anlegen.</span>{' '}
                  <span style={{ color: 'var(--text-secondary)' }}>
                    Der bisherige Termin vom {new Date(initialValues.eventDate + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} wird
                    mit Beschreibung, Strecken und Flyer als vergangene Auflage abgelegt. Die Zahlen beginnen neu,
                    Vormerkungen bleiben, und der Enricher schaut das Event wieder an.
                  </span>
                </span>
              </label>
            </div>
          )}
          {/* Derselbe Vorgang, andere Leser: der Veranstalter **entscheidet** hier nichts. Ob
              vorgerückt wird, sagt beim Freigeben die Regel allein (EventEditService.reviewEdit) —
              ein Haken, der nichts schaltet, wäre eine Behauptung. Und was ihn daran angeht, ist
              nicht das Zählfenster und nicht der Enricher, sondern die eine Frage, die er beim
              Ändern des Datums hat: geht mir etwas verloren? */}
          {rollsOver && !isAdminOrModerator() && (
            <p className="sm:col-span-2 rounded-lg px-3 py-2.5 text-sm"
              style={{ background: 'var(--page-bg)', border: '1px solid var(--surface-border)', color: 'var(--text-secondary)' }}
              data-testid="new-edition-note">
              Der bisherige Termin vom {new Date(initialValues.eventDate + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} bleibt
              als frühere Auflage am Event stehen, mit Beschreibung, Strecken und Flyer. Vormerkungen
              und Aufrufe gehen nicht verloren.
            </p>
          )}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Kategorie</label>
            <select value={form.category} onChange={e => {
                const cat = e.target.value as EventCategory;
                setForm(f => ({ ...f, category: cat,
                  // Die neue Leitkategorie darf nicht zusätzlich als weitere Kategorie stehen.
                  secondaryCategories: f.secondaryCategories.filter(c => c !== cat),
                  // Eventart zurücksetzen, wenn sie nicht zur neuen Kategorie gehört
                  eventType: (EVENT_TYPES_BY_CATEGORY[cat] || []).includes(f.eventType as string) ? f.eventType : '',
                  // Dasselbe für die Arten einzelner Strecken — gemessen an allen Kategorien.
                  distances: distancesForCategories(f.distances, [cat, ...f.secondaryCategories]) }));
              }}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
              <option value="">— Kategorie wählen —</option>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Eventart</label>
            <select value={form.eventType} disabled={!form.category}
              onChange={e => setForm(f => ({ ...f, eventType: e.target.value as EventType | '' }))}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ ...inputStyle, opacity: form.category ? 1 : 0.55, cursor: form.category ? 'pointer' : 'not-allowed' }}>
              {!form.category ? (
                <option value="">— Erst Kategorie wählen —</option>
              ) : (
                <>
                  <option value="">— Unbekannt —</option>
                  {sortEventTypesByLabel(EVENT_TYPES_BY_CATEGORY[form.category] || []).map(key => (
                    <option key={key} value={key}>{EVENT_TYPE_LABELS[key] ?? key}</option>
                  ))}
                </>
              )}
            </select>
          </div>
          {/* Mehrfach-Kategorien: eine Veranstaltung, die z. B. mit Rennrad, Gravel und MTB
              gefahren werden kann, muss nicht dreimal angelegt werden. Die Leitkategorie oben
              bleibt maßgeblich (Farbe, Eventart); hier kommt dazu, was zusätzlich gilt. */}
          {form.category && (
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>
                Weitere Kategorien <span className="font-normal opacity-70">(optional — wenn die Veranstaltung für mehrere offen ist)</span>
              </label>
              <div className="flex flex-wrap gap-4">
                {EVENT_CATEGORY_ORDER.filter(c => c !== form.category).map(c => (
                  <label key={c} className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                    <input
                      type="checkbox"
                      checked={form.secondaryCategories.includes(c)}
                      onChange={e => setForm(f => {
                        const secondaryCategories = e.target.checked
                          ? [...f.secondaryCategories, c]
                          : f.secondaryCategories.filter(x => x !== c);
                        // Fällt eine Kategorie weg, verlieren ihre Strecken-Arten die Auswahl — sonst
                        // bliebe eine unsichtbare Art im State und würde so gespeichert.
                        return { ...f, secondaryCategories,
                          distances: distancesForCategories(f.distances, [f.category, ...secondaryCategories]) };
                      })}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    {CATEGORY_LABELS[c] ?? c}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Startzeit (von)</label>
            <input type="time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Startzeit (bis, optional)</label>
            <input type="time" value={form.startTimeEnd} onChange={e => setForm(f => ({ ...f, startTimeEnd: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Teilnehmer der letzten Auflage (optional)</label>
            <input type="number" min={0} step={1} inputMode="numeric" value={form.participants}
              onChange={e => setForm(f => ({ ...f, participants: e.target.value }))}
              placeholder="z. B. 1200"
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Treffpunkt / Ort</label>
            <input value={form.locationName} onChange={e => setForm(f => ({ ...f, locationName: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}
              placeholder="z.B. Pulsnitzhalle, Grundschule am Park (optional)" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Strasse</label>
            <input value={form.street} onChange={e => setForm(f => ({ ...f, street: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} placeholder="optional" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Hausnr.</label>
            <input value={form.houseNumber} onChange={e => setForm(f => ({ ...f, houseNumber: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} placeholder="optional" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>{form.permanent ? 'PLZ' : 'PLZ *'}</label>
            <input value={form.postalCode} onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>{form.permanent ? 'Stadt' : 'Stadt *'}</label>
            <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>(Bundes)Land</label>
            <select value={form.bundesland} onChange={e => setForm(f => ({ ...f, bundesland: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
              <option value="">Bitte wählen</option>
              <BundeslandOptions />
            </select>
          </div>
          {form.permanent && (
            <p className="sm:col-span-2 text-xs" style={{ color: 'var(--text-secondary)', margin: 0 }}>
              Mehrere Einstiegspunkte? Dann PLZ und Stadt leer lassen und die Einstiegspunkte in der
              Beschreibung nennen. Ohne Ort erscheint das Event nicht in der Umkreissuche.
            </p>
          )}
          {/* Hinweis, keine Sperre: Aus dem (Bundes)Land leitet sich das Land ab, in dem die
              Adresse gesucht wird — passt die PLZ nicht dazu, bleibt das Event ohne Kartenpunkt.
              Sonderfaelle gibt es immer, deshalb darf gespeichert werden. */}
          {postalCodeRegionWarning(form.postalCode, form.bundesland) && (
            <div className="sm:col-span-2 text-xs rounded-lg px-3 py-2"
              style={{ background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e' }}>
              {postalCodeRegionWarning(form.postalCode, form.bundesland)}
            </div>
          )}

          {/* Organizer */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Veranstalter</label>
            <div className="flex rounded-lg overflow-hidden mb-2" style={{ border: '1px solid var(--surface-border)' }}>
              {/* "Neu anlegen" legt direkt per Admin-API an — nur für Admins/Mods sichtbar. */}
              {(isAdminOrModerator() ? (['existing', 'new', 'freetext'] as const) : (['existing', 'freetext'] as const)).map((mode, i) => (
                <button key={mode} type="button"
                  onClick={() => mode === 'new' ? setShowNewOrganizerModal(true) : setOrganizerMode(mode)}
                  className="flex-1 text-xs py-1.5 font-medium transition-colors"
                  style={{
                    background: organizerMode === mode ? '#4f46e5' : 'var(--surface-bg)',
                    color: organizerMode === mode ? 'white' : 'var(--text-muted)',
                    borderLeft: i > 0 ? '1px solid var(--surface-border)' : undefined,
                  }}>
                  {mode === 'existing' ? 'Vorhanden' : mode === 'new' ? 'Neu anlegen' : 'Freitext'}
                </button>
              ))}
            </div>
            {organizerMode === 'freetext' && (
              <input value={form.organizer} onChange={e => setForm(f => ({ ...f, organizer: e.target.value }))}
                placeholder="Name des Veranstalters" className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
            )}
            {organizerMode === 'existing' && (
              <div>
                <input value={organizerSearch} onChange={e => setOrganizerSearch(e.target.value)}
                  placeholder="Suchen..." className="w-full px-3 py-2 rounded-lg text-sm mb-1" style={inputStyle} />
                <select value={selectedOrganizerId ?? ''} size={4}
                  onChange={e => setSelectedOrganizerId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
                  <option value="">— Bitte wählen —</option>
                  {organizers
                    .filter(o => !organizerSearch || o.name.toLowerCase().includes(organizerSearch.toLowerCase()))
                    .map(o => (
                      <option key={o.id} value={o.id}>{o.name}{o.contactPerson ? ` · ${o.contactPerson}` : ''}</option>
                    ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="fullyBookedModal" checked={form.fullyBooked}
                onChange={e => setForm(f => ({ ...f, fullyBooked: e.target.checked }))}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
              <label htmlFor="fullyBookedModal" className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Event ist ausgebucht
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="cancelledModal" checked={form.cancelled}
                onChange={e => setForm(f => ({ ...f, cancelled: e.target.checked }))}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500" />
              <label htmlFor="cancelledModal" className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Event ist abgesagt
              </label>
            </div>
            {!form.permanent && <div className="flex items-center gap-2">
              <input type="checkbox" id="tentativeModal" checked={form.tentative}
                onChange={e => setForm(f => ({ ...f, tentative: e.target.checked }))}
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500" />
              <label htmlFor="tentativeModal" className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Termin ist vorläufig
              </label>
            </div>}
          </div>
          {/* Alles, was auf eine Seite fuehrt, steht beieinander: Veranstaltungs-URL, Anmeldung
              (mit dem Anmelde-Link) und die weiteren Links. Vorher lag der Anmelde-Block oben bei
              den Zeiten und der Link-Editor ganz unten — fuer eine Sache drei Stellen im
              Formular. Der Anmeldestart gehoert dazu, weil er dieselbe Frage beantwortet. */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Veranstaltungs-URL</label>
            <input value={form.organizerUrl} onChange={e => setForm(f => ({ ...f, organizerUrl: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} placeholder="https://..." />
          </div>
          {/* Anmeldestart: jeder darf ihn mitgeben — er steht in der Ausschreibung, die der
              Einreichende vor sich hat. Gezeigt wird er trotzdem nur intern. */}
          <RegistrationFields
            value={{
              mode: form.registrationMode,
              start: form.registrationStart,
              state: form.registrationState,
              // Der Anmelde-Link ist der REGISTRATION-Link des Events — keine zweite Ablage.
              url: registrationLinkOf(form.links),
            }}
            onChange={next => setForm(f => ({
              ...f,
              registrationMode: next.mode,
              registrationStart: next.start,
              registrationState: next.state,
              links: withRegistrationLink(f.links, next.url),
            }))}
            internal={isAdminOrModerator()}
            inputStyle={inputStyle}
          />
          <PriceTiersEditor rows={form.priceTiers} onChange={priceTiers => setForm(f => ({ ...f, priceTiers }))} inputStyle={inputStyle} />
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>
              Weitere Links <span className="font-normal opacity-70">(öffentliche Links erscheinen auf der Event-Seite; breitensport.rad-net.de wird täglich auto-abgeglichen)</span>
            </label>
            <EventLinksEditor
              links={form.links}
              onChange={links => setForm(f => ({ ...f, links }))}
              inputStyle={inputStyle}
            />
          </div>
          {/* Bild und Flyer sind kostenlos (seit 11.09.2026; vom 25.08. bis dahin Premium des
              Veranstalters). Die Felder stehen deshalb in jedem Vorschlag. */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>
              Flyer <span className="font-normal opacity-70">(PDF oder Bild — hochladen, oder verlinken falls online)</span>
            </label>
            <input value={form.flyerUrl} onChange={e => setForm(f => ({ ...f, flyerUrl: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}
              placeholder="https://... (Link zum Flyer, falls der Veranstalter ihn online hat)" />

            {/* Vorschlags-Modus: die Datei geht erst nach dem Einreichen raus (der Vorschlag
                braucht vorher eine Id) und ist bis zur Freigabe nur fuer die Moderation da. */}
            {!canManageImages && (
              <div className="mt-2">
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={e => { pickProposedFlyer(e.target.files?.[0] ?? null); e.target.value = ''; }}
                  className="text-sm"
                />
                {proposedFlyerError && (
                  <p className="text-xs mt-1" style={{ color: '#A82F5A' }}>{proposedFlyerError}</p>
                )}
                {proposedFlyer && (
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs opacity-70">{proposedFlyer.name}</span>
                    <button type="button" onClick={() => pickProposedFlyer(null)}
                      className="text-xs font-semibold px-2 py-1 rounded" style={{ color: '#A82F5A', border: '1px solid var(--surface-border)' }}>
                      Entfernen
                    </button>
                  </div>
                )}
                <p className="text-xs mt-1 opacity-70">
                  Der Flyer erscheint, sobald wir ihn kurz geprüft haben. Max. 4 MB, PDF/JPEG/PNG/WebP.
                </p>
              </div>
            )}

            {/* Upload fuer Mods/Admins nur am gespeicherten Event: die Datei haengt an dessen Id. */}
            {canManageImages && (editingId ? (
              <div className="flex items-center gap-3 flex-wrap mt-2">
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  disabled={flyerUploading}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) uploadFlyer(file);
                    e.target.value = '';
                  }}
                  className="text-sm"
                />
                {flyerUploading && <span className="text-xs opacity-70">Wird hochgeladen…</span>}
                {form.flyerUploadedUrl && (
                  <>
                    <button type="button"
                      onClick={() => downloadEventFile(`/events/${editingId}/flyer`, `${downloadBaseName()}-flyer`, 'Der Flyer')}
                      className="text-xs font-semibold px-2 py-1 rounded"
                      style={{ color: 'var(--focus-color)', border: '1px solid var(--surface-border)' }}>
                      Flyer herunterladen
                    </button>
                    <button type="button" onClick={deleteFlyer}
                      className="text-xs font-semibold px-2 py-1 rounded" style={{ color: '#A82F5A', border: '1px solid var(--surface-border)' }}>
                      Flyer löschen
                    </button>
                  </>
                )}
              </div>
            ) : (
              <p className="text-xs opacity-70 mt-2">Flyer-Upload ist erst möglich, nachdem das Event gespeichert wurde.</p>
            ))}

            {form.flyerUploadedUrl && form.flyerUrl && (
              <p className="text-xs mt-1 opacity-70">
                Hochgeladener Flyer und Link sind beide hinterlegt — gezeigt wird der hochgeladene.
              </p>
            )}

            {flyerPreviewSrc && (
              flyerPreviewIsImage ? (
                <img src={flyerPreviewSrc} alt="Flyer-Vorschau"
                  className="mt-2 rounded-lg max-h-40 object-contain"
                  style={{ border: '1px solid var(--surface-border)' }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              ) : (
                <a href={flyerPreviewSrc} target="_blank" rel="noopener noreferrer"
                  className="inline-block mt-2 text-xs font-semibold px-2 py-1 rounded"
                  style={{ color: 'var(--focus-color)', border: '1px solid var(--surface-border)' }}>
                  Flyer öffnen (PDF) ↗
                </a>
              )
            )}
          </div>
          {/* Vorschlags-Modus: Foto anhängen statt Bildverwaltung. Die Datei wird erst nach dem
              Einreichen hochgeladen (der Vorschlag braucht vorher eine ID) und ist nur für die
              Moderation sichtbar, bis sie freigegeben wird. */}
          {!canManageImages ? (
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>
                Foto vorschlagen <span className="font-normal opacity-70">(optional; erscheint erst nach Prüfung durch die Redaktion)</span>
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={e => { pickProposedImage(e.target.files?.[0] ?? null); e.target.value = ''; }}
                className="text-sm"
              />
              {proposedImageError && (
                <p className="text-xs mt-1" style={{ color: '#A82F5A' }}>{proposedImageError}</p>
              )}
              {proposedImagePreview && (
                <div className="mt-2">
                  <div style={{
                    width: '100%', maxWidth: 420, aspectRatio: '1100 / 300',
                    borderRadius: 12, border: '1px solid var(--surface-border)',
                    backgroundImage: `url("${proposedImagePreview}")`, backgroundSize: 'cover', backgroundPosition: 'center',
                  }} />
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs opacity-70">{proposedImage?.name}</span>
                    <button type="button" onClick={() => pickProposedImage(null)}
                      className="text-xs font-semibold px-2 py-1 rounded" style={{ color: '#A82F5A', border: '1px solid var(--surface-border)' }}>
                      Entfernen
                    </button>
                  </div>
                  <input value={proposedImageCredit} onChange={e => setProposedImageCredit(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm mt-2" style={inputStyle}
                    placeholder="Bildnachweis, z. B. „Foto: RSV Musterstadt“" />
                  <p className="text-xs mt-1 opacity-70">
                    Bitte nur Fotos hochladen, an denen du die Rechte hast. Max. 2 MB, JPEG/PNG/WebP.
                  </p>
                </div>
              )}
            </div>
          ) : (
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>
              Event-Bild <span className="font-normal opacity-70">(Hero der Event-Card; ohne Bild wird ein Motiv aus den Streckendaten erzeugt)</span>
            </label>

            {editingId ? (
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={imageUploading}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) uploadImage(file);
                    e.target.value = '';
                  }}
                  className="text-sm"
                />
                {imageUploading && <span className="text-xs opacity-70">Wird hochgeladen…</span>}
                {form.imageUploadedUrl && (
                  <>
                    <button type="button"
                      onClick={() => downloadEventFile(`/events/${editingId}/image`, `${downloadBaseName()}-bild`, 'Das Bild')}
                      className="text-xs font-semibold px-2 py-1 rounded"
                      style={{ color: 'var(--focus-color)', border: '1px solid var(--surface-border)' }}>
                      Bild herunterladen
                    </button>
                    <button type="button" onClick={deleteImage}
                      className="text-xs font-semibold px-2 py-1 rounded" style={{ color: '#A82F5A', border: '1px solid var(--surface-border)' }}>
                      Bild löschen
                    </button>
                  </>
                )}
              </div>
            ) : (
              <p className="text-xs opacity-70">Bild-Upload ist erst möglich, nachdem das Event gespeichert wurde.</p>
            )}

            {heroPreviewSrc && (
              <div className="mt-2">
                {/* Fokuspunkt nur beim Foto: Logo und Schriftzug werden nicht zugeschnitten,
                    der Punkt hätte dort keine Wirkung. */}
                {previewShape === 'photo' && (
                <>
                <div
                  onClick={e => {
                    const r = e.currentTarget.getBoundingClientRect();
                    const x = Math.round(((e.clientX - r.left) / r.width) * 100);
                    const y = Math.round(((e.clientY - r.top) / r.height) * 100);
                    setForm(f => ({
                      ...f,
                      imageFocusX: Math.min(100, Math.max(0, x)),
                      imageFocusY: Math.min(100, Math.max(0, y)),
                    }));
                  }}
                  title="Klicken: Bildmitte festlegen — dieser Punkt bleibt in jedem Ausschnitt sichtbar"
                  style={{ position: 'relative', cursor: 'crosshair', display: 'inline-block', lineHeight: 0 }}
                >
                  <img src={heroPreviewSrc} alt="Event-Bild"
                    className="rounded-lg"
                    style={{ maxHeight: 200, display: 'block', border: '1px solid var(--surface-border)' }} />
                  <span style={{
                    position: 'absolute',
                    left: `${form.imageFocusX}%`, top: `${form.imageFocusY}%`,
                    width: 16, height: 16, marginLeft: -8, marginTop: -8,
                    borderRadius: '50%', border: '2px solid #fff', background: 'rgba(146,44,87,0.85)',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.35)', pointerEvents: 'none',
                  }} />
                </div>
                <div className="text-xs mt-1 flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                  <span>Bildausschnitt: klick auf das Motiv ({form.imageFocusX}% / {form.imageFocusY}%)</span>
                  <button type="button"
                    onClick={() => setForm(f => ({ ...f, imageFocusX: 50, imageFocusY: 50 }))}
                    className="underline">
                    zurücksetzen
                  </button>
                </div>
                </>
                )}

                {/* Vorschau genau so, wie die Event-Seite das Bild einsetzt — je nach erkanntem
                    Typ zugeschnittener Hero, voller Schriftzug oder Logo-Kachel (siehe
                    utils/heroImageShape). Die Card zeigt nie ein Foto, sondern immer das
                    generierte Motiv (EventCardHero); eine Card-Vorschau wäre also ein Ausschnitt,
                    den niemand je zu sehen bekommt. */}
                <div className="mt-2">
                  <div className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>
                    {previewShape === 'logo' && 'Als Logo erkannt — erscheint als Kachel neben den Eckdaten:'}
                    {previewShape === 'banner' && 'Als Schriftzug erkannt — erscheint oben in voller Breite, ohne Zuschnitt:'}
                    {previewShape === 'photo' && 'So erscheint der Ausschnitt auf der Event-Seite:'}
                    {previewShape === null && 'Bild wird geprüft …'}
                  </div>
                  {previewShape === 'logo' ? (
                    <div style={{
                      width: '100%', maxWidth: 260, padding: 14, textAlign: 'center',
                      borderRadius: 12, border: '1px solid var(--surface-border)', background: '#fff',
                    }}>
                      <img src={heroPreviewSrc} alt="" style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', display: 'inline-block' }} />
                    </div>
                  ) : previewShape === 'banner' ? (
                    <img src={heroPreviewSrc} alt=""
                      style={{
                        width: '100%', maxWidth: 420, maxHeight: 160, objectFit: 'contain',
                        display: 'block', borderRadius: 12, border: '1px solid var(--surface-border)', background: '#fff',
                      }} />
                  ) : (
                    <div style={{
                      width: '100%', maxWidth: 420, aspectRatio: '1100 / 300',
                      borderRadius: 12, border: '1px solid var(--surface-border)',
                      backgroundImage: `url("${heroPreviewSrc}")`, backgroundSize: 'cover',
                      backgroundPosition: `${form.imageFocusX}% ${form.imageFocusY}%`,
                    }} />
                  )}
                </div>
              </div>
            )}

            <input value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm mt-2" style={inputStyle}
              placeholder="oder externe Bild-URL: https://..." />
            {form.imageUploadedUrl && form.imageUrl && (
              <p className="text-xs mt-1 opacity-70">Das hochgeladene Bild hat Vorrang vor dieser URL.</p>
            )}

            <input value={form.imageCredit} onChange={e => setForm(f => ({ ...f, imageCredit: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm mt-2" style={inputStyle}
              placeholder="Bildnachweis, z. B. „Foto: RSV Musterstadt“" />

            <div className="flex items-center gap-2 mt-2">
              <input type="checkbox" id="imageAiGenerated" checked={form.imageAiGenerated}
                onChange={e => setForm(f => ({ ...f, imageAiGenerated: e.target.checked }))}
                className="rounded border-gray-300" />
              <label htmlFor="imageAiGenerated" className="text-sm" style={{ color: 'var(--text-primary)' }}>
                Bild ist KI-generiert <span className="opacity-70">(wird auf der Event-Seite gekennzeichnet)</span>
              </label>
            </div>
          </div>
          )}
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--form-label)' }}>Beschreibung</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3} className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
        </div>

        {/* Komoot Collection Import */}
        <div className="mb-4 p-3 rounded-lg" style={{ background: 'var(--page-bg)', border: '1px solid var(--surface-border)' }}>
          <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--form-label)' }}>Komoot Collection Import</label>
          <div className="flex gap-2">
            <input
              value={form.komootCollectionUrl}
              onChange={e => { setForm(f => ({ ...f, komootCollectionUrl: e.target.value })); setKomootError(null); }}
              placeholder="https://www.komoot.com/de-de/collection/..."
              className="flex-1 px-3 py-2 rounded-lg text-sm" style={inputStyle}
            />
            <button
              onClick={importKomootCollection}
              disabled={!form.komootCollectionUrl || komootImporting}
              className="px-3 py-2 text-sm font-medium rounded-lg disabled:opacity-50 whitespace-nowrap"
              style={{ background: 'var(--accent-warm)', color: '#fff' }}
            >
              {komootImporting ? 'Lädt...' : 'Importieren'}
            </button>
          </div>
          {komootError && <p className="text-xs mt-1" style={{ color: 'var(--badge-red-text)' }}>{komootError}</p>}
        </div>

        {/* Distances */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold" style={{ color: 'var(--form-label)' }}>Distanzen</label>
            <button onClick={addDistance} className="text-xs font-medium" style={{ color: 'var(--accent-warm)' }}>
              + Distanz hinzufügen
            </button>
          </div>
          {form.distances.map((d, idx) => (
            <div key={idx} className="mb-3 p-3 rounded-lg"
              style={{ background: 'var(--page-bg)', border: '1px solid var(--surface-border)' }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-1">
                  <input type="number" value={d.distanceKm || ''} placeholder="km"
                    onChange={e => updateDistance(idx, 'distanceKm', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
                </div>
                <div className="flex-1">
                  <input type="number" value={d.elevationMeters || ''} placeholder="Höhenmeter"
                    onChange={e => updateDistance(idx, 'elevationMeters', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
                </div>
                <div className="flex-1 flex gap-1" title="Zeitlimit (z. B. 24h-Rennen)">
                  <input type="number" min="0" value={d.durationMinutes ? Math.floor(d.durationMinutes / 60) || '' : ''} placeholder="Std."
                    onChange={e => { const h = parseInt(e.target.value) || 0; const dm = h * 60 + ((d.durationMinutes ?? 0) % 60); updateDistance(idx, 'durationMinutes', dm > 0 ? dm : 0); }}
                    className="w-full px-2 py-2 rounded-lg text-sm" style={inputStyle} />
                  <input type="number" min="0" max="59" value={d.durationMinutes ? d.durationMinutes % 60 || '' : ''} placeholder="Min."
                    onChange={e => { const m = parseInt(e.target.value) || 0; const dm = Math.floor((d.durationMinutes ?? 0) / 60) * 60 + m; updateDistance(idx, 'durationMinutes', dm > 0 ? dm : 0); }}
                    className="w-full px-2 py-2 rounded-lg text-sm" style={inputStyle} />
                </div>
                {form.distances.length > 1 && (
                  <button onClick={() => removeDistance(idx)} className="p-1 flex-shrink-0"
                    style={{ color: 'var(--badge-red-text)' }}>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex gap-2 mb-2">
                <input value={d.label || ''} placeholder="Bezeichnung (z.B. Hauptroute, Kurzetappe) — optional"
                  onChange={e => updateDistance(idx, 'label', e.target.value)}
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg text-sm" style={inputStyle} />
                {/* Eigene Startzeit je Strecke: RTFs starten gestaffelt, die lange Runde zuerst.
                    Leer heißt „wie das Event". Ohne das Feld schickte ein Verein am 02.10.2026
                    je Strecke einen eigenen Vorschlag mit geänderter Event-Startzeit. */}
                <label className="flex items-center gap-1.5 text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                  Start
                  <input type="time" value={d.startTime || ''} aria-label="Startzeit dieser Strecke"
                    data-testid={`distance-start-time-${idx}`}
                    title={form.startTime ? `Leer lassen, wenn die Strecke um ${form.startTime} Uhr startet` : 'Leer lassen, wenn die Strecke mit dem Event startet'}
                    onChange={e => updateDistance(idx, 'startTime', e.target.value || null)}
                    className="px-2 py-2 rounded-lg text-sm" style={inputStyle} />
                </label>
              </div>
              {/* Eigene Eventart je Strecke: Gravel-Tour und Gravel-Race im selben Event, ohne es
                  doppelt anzulegen. Leer heißt „wie das Event". */}
              {form.category && (
                <select value={d.eventType ?? ''} aria-label="Eventart dieser Strecke"
                  data-testid={`distance-event-type-${idx}`}
                  onChange={e => updateDistance(idx, 'eventType', e.target.value || null)}
                  className="w-full px-3 py-2 rounded-lg text-sm mb-2" style={inputStyle}>
                  <option value="">
                    {form.eventType
                      ? `Eventart wie das Event (${EVENT_TYPE_LABELS[form.eventType] ?? form.eventType})`
                      : 'Eventart wie das Event'}
                  </option>
                  {sortEventTypesByLabel(eventTypesForCategories([form.category, ...form.secondaryCategories]))
                    .filter(key => key !== form.eventType)
                    .map(key => (
                      <option key={key} value={key}>{EVENT_TYPE_LABELS[key] ?? key}</option>
                    ))}
                </select>
              )}
              <input value={d.routeUrl || ''} placeholder="Strecken-URL (Komoot oder AllTrails) — optional"
                onChange={e => updateDistance(idx, 'routeUrl', e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />

              {/* Vorschlags-Modus: Datei wählen, hochgeladen wird sie mit dem Vorschlag. Sie
                  ersetzt eine vorhandene Strecke erst, wenn ein Prüfer den Vorschlag freigibt. */}
              {!canManageImages ? (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>GPX-Strecke:</span>
                  {distanceHasGpx(d) && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">vorhanden</span>
                  )}
                  {proposedGpxByRow[idx] ? (
                    <>
                      <span className="text-xs" style={{ color: 'var(--text-primary)' }}>{proposedGpxByRow[idx].name}</span>
                      <button type="button" onClick={() => pickProposedGpx(idx, null)} className="text-xs underline"
                        style={{ color: 'var(--badge-red-text)' }}>
                        entfernen
                      </button>
                    </>
                  ) : (
                    <label className="text-xs underline cursor-pointer" style={{ color: 'var(--accent-warm)' }}>
                      {distanceHasGpx(d) ? 'andere GPX vorschlagen (max 5 MB)' : 'GPX vorschlagen (max 5 MB)'}
                      <input type="file" accept=".gpx,application/gpx+xml,application/xml" className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; if (f) pickProposedGpx(idx, f); e.target.value = ''; }} />
                    </label>
                  )}
                </div>
              ) : d.id != null ? (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>GPX-Strecke:</span>
                  {distanceHasGpx(d) ? (
                    <>
                      <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">vorhanden</span>
                      <button type="button" onClick={() => downloadGpx(d)} className="text-xs underline"
                        style={{ color: 'var(--accent-warm)' }}>
                        herunterladen
                      </button>
                      <label className="text-xs underline cursor-pointer" style={{ color: 'var(--accent-warm)' }}>
                        austauschen
                        <input type="file" accept=".gpx,application/gpx+xml,application/xml" className="hidden"
                          onChange={e => { const f = e.target.files?.[0]; if (f) uploadGpx(d.id!, f); e.target.value = ''; }} />
                      </label>
                      <button type="button" onClick={() => deleteGpx(d.id!)} className="text-xs underline" style={{ color: 'var(--badge-red-text)' }}>
                        entfernen
                      </button>
                    </>
                  ) : (
                    <label className="text-xs underline cursor-pointer" style={{ color: 'var(--accent-warm)' }}>
                      GPX hochladen (max 5 MB)
                      <input type="file" accept=".gpx,application/gpx+xml,application/xml" className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; if (f) uploadGpx(d.id!, f); e.target.value = ''; }} />
                    </label>
                  )}
                  {gpxUploadingId === d.id && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>lädt…</span>}
                </div>
              ) : (
                <p className="mt-2 text-xs italic" style={{ color: 'var(--text-muted)' }}>
                  GPX-Upload nach dem ersten Speichern verfügbar
                </p>
              )}
            </div>
          ))}
          {!canManageImages && Object.keys(proposedGpxByRow).length > 0 && (
            <p className="text-xs italic" style={{ color: 'var(--text-muted)' }}>
              Die Strecke wird zusammen mit dem Vorschlag eingereicht und erscheint erst nach der
              Prüfung auf der Event-Seite.
            </p>
          )}
        </div>

        {extraFooter}

        <div className={`flex justify-end gap-3${imDialog ? ' admin-dialog__aktionen ev-form-aktionen' : ''}`}>
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            style={{ color: 'var(--text-secondary)', background: 'var(--page-bg)', border: '1px solid var(--surface-border)' }}>
            Abbrechen
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-brand-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50">
            {saving ? 'Speichern...' : saveLabel}
          </button>
        </div>
      </div>

      <OrganizerFormModal
        show={showNewOrganizerModal}
        onClose={() => setShowNewOrganizerModal(false)}
        onSaved={(o) => {
          setShowNewOrganizerModal(false);
          onOrganizersRefresh();
          setOrganizerMode('existing');
          setSelectedOrganizerId(o.id);
        }}
        zIndex={60}
      />
    </>
  );
};

/** Dasselbe Formular als Fenster ueber der Seite — fuer die Admin-Listen. */
const EventFormModal: React.FC<EventFormModalProps> = ({ show, ...rest }) => {
  if (!show) return null;
  return (
    <div className="admin-dialog ev-form-dialog fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="admin-dialog__panel w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl p-6"
        style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
        <EventForm {...rest} showOpenLink imDialog />
      </div>
    </div>
  );
};

export default EventFormModal;
